import { Alert, Badge, Card, CardBody, CardHeader, PageHeader, PageLoader, Stat } from '@alicerce/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { FilePicker, useUpload } from '@/components/upload';
import { errorMessage } from '@/lib/api';
import { useApi } from '@/lib/session';

interface ImportReport {
  id: string;
  status: 'QUEUED' | 'PROCESSING' | 'DONE' | 'FAILED';
  total: number;
  created: number;
  errors: string[];
  finishedAt?: string;
}

const TEMPLATE = [
  'nome;preco;custo;categoria;status;sku;opcoes;peso_g;ncm;descricao',
  'Camiseta básica;49,90;20,00;Camisetas;ativo;;cor:Azul|tamanho:M;180;61091000;Algodão',
  'Camiseta básica;49,90;20,00;Camisetas;ativo;;cor:Preto|tamanho:M;180;61091000;Algodão',
  'Caneca;25,00;9,50;Casa;ativo;CANECA-01;;350;;',
].join('\n');

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([`\ufeff${TEMPLATE}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'modelo-produtos.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function ImportProductsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formatOf = (f: File) => (/\.xlsx$/i.test(f.name) ? 'xlsx' : 'csv');
  const uploader = useUpload((file) => api.post('/v1/core/products/import/upload-url', { format: formatOf(file) }), {
    maxBytes: 20 * 1024 * 1024,
    accept: () => true,
    typeMessage: '',
  });

  async function onFiles(files: File[]) {
    const file = files[0];
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      uploader.setError('Envie a planilha em CSV ou XLSX.');
      return;
    }
    const key = await uploader.upload(file);
    if (!key) return;
    try {
      const r = await api.post<{ importId: string }>('/v1/core/products/import', { fileKey: key, format: formatOf(file) });
      await queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate(`/produtos/importacoes/${r.importId}`);
    } catch (e) {
      uploader.setError(errorMessage(e));
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Importar produtos" description="Cadastre muitos produtos de uma vez com uma planilha." />
      <Card>
        <CardBody className="space-y-4">
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>
              Baixe o{' '}
              <button type="button" className="font-medium text-brand-700 hover:underline" onClick={downloadTemplate}>
                modelo de planilha
              </button>{' '}
              e preencha uma linha por produto (ou por variante, repetindo o nome).
            </li>
            <li>As variantes vão na coluna "opcoes", assim: cor:Azul|tamanho:M.</li>
            <li>Envie o arquivo em CSV ou XLSX, com até 5.000 linhas.</li>
          </ol>
          {uploader.error && <Alert tone="danger">{uploader.error}</Alert>}
          <FilePicker accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" busy={uploader.busy} onFiles={(f) => void onFiles(f)}>
            Arraste a planilha aqui ou
          </FilePicker>
        </CardBody>
      </Card>
    </div>
  );
}

export function ImportReportPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const q = useQuery({
    queryKey: ['product-import', id],
    queryFn: () => api.get<ImportReport>(`/v1/core/products/import/${id}`),
    refetchInterval: (query) => (query.state.data && ['DONE', 'FAILED'].includes(query.state.data.status) ? false : 2000),
  });
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const r = q.data;
  const running = r.status === 'QUEUED' || r.status === 'PROCESSING';
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader title="Importação de produtos" description={<Link to="/produtos" className="text-brand-700 hover:underline">Ver produtos</Link>} />
      <Card>
        <CardHeader
          title="Resultado"
          actions={<Badge tone={r.status === 'DONE' ? 'success' : r.status === 'FAILED' ? 'danger' : 'info'}>{running ? 'Processando…' : r.status === 'DONE' ? 'Concluída' : 'Falhou'}</Badge>}
        />
        <CardBody className="grid grid-cols-3 gap-4">
          <Stat label="Linhas lidas" value={r.total} />
          <Stat label="Produtos criados" value={r.created} tone="positive" />
          <Stat label="Erros" value={r.errors.length} tone={r.errors.length ? 'negative' : 'neutral'} />
        </CardBody>
      </Card>
      {r.errors.length > 0 && (
        <Card>
          <CardHeader title="Linhas com erro" description="Corrija na planilha e envie só essas linhas de novo." />
          <CardBody>
            <ul className="max-h-96 list-disc space-y-1 overflow-y-auto pl-5 text-sm">
              {r.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
